import React, { useState } from "react";
import { Modal, Form, Input, Upload, Button, Image, notification } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import Joi from "joi";
import axios from "axios";
import config from "../../../../../config/config";

const LaxmisKitchenCategoryCreate = ({
  isVisible,
  handleClose,
  moduleName,
  base_url,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null); // ✅ preview

  const joiOptions = config.joiOptions;

  /* ===================== VALIDATION ===================== */
  const validate = (values) => {
    const schema = Joi.object({
      categoryTitle: Joi.string().min(1).required().label("Category Title"),
    });

    const { error } = schema.validate(values, joiOptions);
    const errors = {};

    if (error) {
      error.details.forEach((item) => {
        errors[item.path[0]] = item.message;
      });
    }

    if (!imageFile) {
      errors.categoryImage = "Category image is required";
    }

    return errors;
  };

  /* ===================== SUBMIT ===================== */
  const handleSubmit = async (values) => {
    const errors = validate(values);
    setFormError(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("categoryTitle", values.categoryTitle);
      formData.append("categoryImageUrl", imageFile); // important: backend expects this
      formData.append("type", "CATEGORY");

      await axios.post(`${base_url}/recipe/category/new`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      notification.success({
        message: `${moduleName} created successfully`,
      });

      form.resetFields();
      setImageFile(null);
      setImagePreview(null);
      handleClose();
    } catch (err) {
      notification.error({
        message: "Create failed",
        description: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  /* ===================== IMAGE HANDLER ===================== */
  const handleImageChange = (file) => {
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
    return false; // prevent auto upload
  };

  return (
    <Modal
      title={`Add ${moduleName}`}
      visible={isVisible}
      onCancel={handleClose}
      okText="Create"
      cancelText="Cancel"
      confirmLoading={loading}
      onOk={() => {
        form
          .validateFields()
          .then((values) => handleSubmit(values))
          .catch(() => {});
      }}
      width={600}
      destroyOnClose
    >
      <Form layout="vertical" form={form}>
        {/* CATEGORY TITLE */}
        <Form.Item
          label="Category Title"
          name="categoryTitle"
          extra="Category title must be unique"
          required
          {...(formError.categoryTitle && {
            help: formError.categoryTitle,
            validateStatus: "error",
          })}
        >
          <Input placeholder="Enter category title" />
        </Form.Item>

        {/* IMAGE UPLOAD */}
        <Form.Item
          label="Category Image"
          required
          extra="Recommended size: 669 x 661 px"
          {...(formError.categoryImage && {
            help: formError.categoryImage,
            validateStatus: "error",
          })}
        >
          <Upload
            beforeUpload={handleImageChange}
            maxCount={1}
            accept="image/*"
            showUploadList={false} // hide default AntD preview
          >
            <Button icon={<UploadOutlined />}>Select Image</Button>
          </Upload>

          {/* PREVIEW */}
          {imagePreview && (
            <div style={{ marginTop: 10 }}>
              <Image
                src={imagePreview}
                width={120}
                height={120}
                style={{ objectFit: "cover", borderRadius: 6 }}
                alt="Category Preview"
              />
            </div>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default LaxmisKitchenCategoryCreate;
